/**
 * The dispatch board's health strip (docs/05-admin/08 §3.1 item 2): compact tiles from
 * `/v1/admin/dispatch/health` for the city the board is filtered to.
 *
 * **Built from the spec, not a design** (owner ruling 2026-10-26), with the console's StatTile.
 *
 * - **One city:** its acceptance rate, median time to accept and how many bookings ran out of
 *   pandits. A city that dispatched nothing in the window is absent from the read, and the strip
 *   says so rather than drawing zeros (the health screen's rule).
 * - **All cities:** the per-city rate is over answered offers, which the read does not carry, so
 *   the rates cannot be pooled. The strip names the worst city instead (the server orders worst
 *   first) and sums only the exhaustion count, which does add.
 * - **Open waves** are the waves out right now, not over the window, so they add across cities.
 *
 * Rate and time tiles open the health screen; the exhaustion tile opens the Escalations tab,
 * which is the reassignment queue.
 *
 * `DispatchOpsTiles` is the landing dashboard's half of the same read (docs/05-admin/05 §3.1
 * item 5): how many cities are in wave trouble, and the worst acceptance rate.
 */
import { StatTile } from '../components/kit';
import { useConsole } from '../app/console';
import { useDispatchHealth, type CityDispatchHealth } from '../lib/api/hooks/dispatch';
import { asDuration, asPercent, inWaveTrouble, type HealthLines } from './DispatchHealth';

export const STRIP_WINDOW_DAYS = 7;

/** Red on a tile: under the alarm line ops set (`dispatch.health_acceptance_alarm`). */
function underAlarm(rate: number | null | undefined, lines: HealthLines | undefined): boolean {
  return lines !== undefined && rate !== null && rate !== undefined && rate < lines.acceptance_alarm;
}

export function HealthStrip({ cityId, onEscalations }: { cityId: number | undefined; onEscalations: () => void }) {
  const { go } = useConsole();
  const health = useDispatchHealth(STRIP_WINDOW_DAYS);
  // Before the read lands, and while it fails, the board is still the board: no strip at all.
  if (!health.data?.items) return null;

  const rows: CityDispatchHealth[] = health.data.items;
  const lines = health.data;
  const window = `last ${STRIP_WINDOW_DAYS} days`;
  const tiles = (
    rate: CityDispatchHealth | undefined,
    rateNote: string,
    median: string,
    medianNote: string,
    openWaves: number,
    exhausted: number,
  ) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--sp-md)' }}>
      <StatTile
        label="Acceptance"
        value={asPercent(rate?.acceptance_rate)}
        note={rateNote}
        tone={underAlarm(rate?.acceptance_rate, lines) ? 'error' : 'default'}
        onClick={() => go('health')}
      />
      <StatTile label="Median to accept" value={median} note={medianNote} onClick={() => go('health')} />
      <StatTile label="Open waves" value={openWaves} note="out right now, waiting on pandits" onClick={() => go('health')} />
      <StatTile
        label="Ran out of pandits"
        value={exhausted}
        note={`${window} — open the escalations`}
        tone={exhausted >= lines.exhaustion_warn ? 'error' : 'default'}
        onClick={onEscalations}
      />
    </div>
  );

  if (cityId === undefined) {
    const worst = rows[0];
    const slowest = rows
      .filter((r) => r.median_seconds_to_accept !== null && r.median_seconds_to_accept !== undefined)
      .sort((a, b) => b.median_seconds_to_accept! - a.median_seconds_to_accept!)[0];
    return tiles(
      worst,
      worst ? `lowest city: ${worst.city_name}, ${window}` : `no city dispatched in the ${window}`,
      asDuration(slowest?.median_seconds_to_accept),
      slowest ? `slowest city: ${slowest.city_name}` : 'nothing accepted yet',
      rows.reduce((sum, r) => sum + r.open_waves, 0),
      rows.reduce((sum, r) => sum + r.exhausted, 0),
    );
  }

  const city = rows.find((r) => r.city_id === cityId);
  if (!city) {
    return tiles(undefined, `nothing dispatched here in the ${window}`, '—', 'no waves went out', 0, 0);
  }
  return tiles(
    city,
    `of ${city.offers_sent} offers, ${window}`,
    asDuration(city.median_seconds_to_accept),
    window,
    city.open_waves,
    city.exhausted,
  );
}

export function DispatchOpsTiles() {
  const { go } = useConsole();
  const health = useDispatchHealth(STRIP_WINDOW_DAYS);
  const rows = health.data?.items;
  const lines = health.data;
  const worst = rows?.[0];
  const trouble = lines ? rows?.filter((r) => inWaveTrouble(r, lines)).length : undefined;
  const window = `last ${STRIP_WINDOW_DAYS} days`;
  return (
    <>
      <StatTile
        label="Cities in wave trouble"
        value={trouble ?? '—'}
        note={
          lines
            ? `acceptance under ${Math.round(lines.acceptance_alarm * 100)}% or a booking ran out, ${window}`
            : 'dispatch health unavailable'
        }
        tone={(trouble ?? 0) > 0 ? 'error' : 'default'}
        onClick={() => go('health')}
      />
      <StatTile
        label="Worst acceptance"
        value={rows ? asPercent(worst?.acceptance_rate) : '—'}
        note={worst ? worst.city_name : rows ? `no city dispatched, ${window}` : 'dispatch health unavailable'}
        tone={underAlarm(worst?.acceptance_rate, lines) ? 'error' : 'default'}
        onClick={() => go('health')}
      />
    </>
  );
}
